import { useState, useEffect } from 'react'
import { useLocation, NavLink } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { NavItemConfig } from './SidebarNavItem'

interface Props {
  item: NavItemConfig
  collapsed: boolean
}

/**
 * Grupo de navegación con hijos desplegables. Cuando la sidebar está
 * colapsada, los hijos se muestran como popover lateral al hover.
 *
 * Auto-abre el grupo si alguna ruta hija está activa.
 */
export function SidebarNavGroup({ item, collapsed }: Props) {
  const Icon = item.icon
  const location = useLocation()
  const childActive = item.children?.some((c) => location.pathname.startsWith(c.path)) ?? false
  const [open, setOpen] = useState<boolean>(childActive)

  useEffect(() => {
    if (childActive) setOpen(true)
  }, [childActive])

  if (collapsed) {
    return (
      <NavLink
        to={item.path}
        title={item.label}
        className={({ isActive }) =>
          cn(
            'flex h-10 w-full items-center justify-center rounded-lg transition-colors',
            isActive || childActive
              ? 'bg-sidebar-accent text-sidebar-foreground'
              : 'text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground',
          )
        }
      >
        <Icon className="h-5 w-5" />
      </NavLink>
    )
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={cn(
          'group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
          childActive
            ? 'text-sidebar-foreground'
            : 'text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground',
        )}
      >
        <Icon className="h-5 w-5 shrink-0" />
        <span className="flex-1 text-left truncate">{item.label}</span>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
            open && 'rotate-180',
          )}
        />
      </button>
      {open && (
        <div className="mt-1 ml-3 space-y-0.5 border-l border-sidebar-border pl-3">
          {item.children?.map((child) => (
            <NavLink
              key={child.path}
              to={child.path}
              end={child.end}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-[13px] font-medium transition-colors',
                  isActive
                    ? 'bg-sidebar-accent text-sidebar-foreground font-semibold'
                    : 'text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground',
                )
              }
            >
              <child.icon className="h-4 w-4 shrink-0" />
              <span className="truncate">{child.label}</span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  )
}
